import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomButton from 'components/common/CustomButton';
import { toast } from 'react-toastify';
import CustomInput from 'components/common/CustomInput';
import { correctEmail } from 'components/common/regex';
import axios from 'axios';
import { encrypt, setEncryptedCookie } from 'components/encryptDecrpt';
import { useSetAtom } from 'jotai';
import { useGetUser, userAtom } from 'atom/user';
import PageTemplate from 'components/template/PageTemplate';
import { ReactComponent as LogoB } from 'assets/common/logoB.svg'
import { ReactComponent as Bar } from 'assets/login/bar.svg'
import { ReactComponent as EyeOn } from 'assets/common/eyeOn.svg'
import { ReactComponent as EyeOff } from 'assets/common/eyeOff.svg'
import { ReactComponent as CheckBoxOn } from 'assets/common/checkBoxOn.svg'
import { ReactComponent as CheckBoxOff } from 'assets/common/checkBoxOff.svg'

const Login = () => {
    interface ILoginInfo {
        username: string;
        password: string;
    }
    const user = useGetUser();
    const setUser = useSetAtom(userAtom)
    const navigate = useNavigate();
    const [clear, setClear] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [loginInfo, setLoginInfo] = useState<ILoginInfo>({
        username: '',
        password: ''
    })
    const [pwShow, setPwShow] = useState<boolean>(false);
    const [loginError, setLoginError] = useState<boolean>(false);
    const [keep, setKeep] = useState<boolean>(false);

    useEffect(() => {
        if (user.id) {
            toast.success('로그인상태입니다.')
            navigate('/')
        }
    }, [user])

    useEffect(() => {
        setLoginError(false)
        loginInfo.username && loginInfo.password ? setClear(true) : setClear(false);
    }, [loginInfo]);

    const goLogin = () => {
        if (loginInfo.username === '') {
            toast.error('이메일을 입력해주세요.');
            return;
        }
        else if (!correctEmail(loginInfo.username)) {
            toast.error('올바르지 않는 이메일 입니다.');
            return;
        }
        else if (loginInfo.password === '') {
            toast.error('비밀번호를 입력해주세요.');
            return;
        }

        setLoading(true)

        postLogin(loginInfo).then(res => {
            if (res.data.code === 200) {
                setEncryptedCookie('token', res.data.data.token)
                sessionStorage.setItem('login', 'true');
                setUser(encrypt(JSON.stringify(res.data.data)))
                navigate('/', { replace: true });
            }
            else if (res.data.code === 400) {
                setLoginError(true);
            }
            else if (res.data.code === 401) {
                toast.error('비활성화된 계정입니다. 관리자에게 문의해주세요.');
            }
            else {
                toast.error('관리자에게 문의해주세요.');
            }
        })
        setLoading(false)
    }

    const goSignUp = () => {
        navigate('/signup');
    }

    const goPwRest = () => {
        navigate('/resetpw');
    }

    const postLogin = async (reqData: object) => {
        const url = `${process.env.REACT_APP_API_URL}signin`
        return await axios.post(url, reqData)
    }

    return (
        <PageTemplate background='bg-[#f8f8f8]' isFooter={false}>
            <div className='flex flex-col justify-center items-center gap-4 py-30 mobile:py-0 h-dvh mobile:h-[calc(100dvh-80px)]'>
                <form className="flex flex-col justify-center items-center gap-4 bg-white px-10 mobile:px-6 py-15 mobile:py-10 rounded-3xl w-[600px] mobile:w-9/10">
                    <div className='mb-15 mobile:mb-6'>
                        <LogoB className='mobile:w-40' />
                    </div>
                    <CustomInput
                        type='email'
                        name="username"
                        placeholder='이메일을 입력해주세요'
                        onChange={e => {
                            setLoginInfo({
                                ...loginInfo,
                                username: e.target.value
                            })
                        }}
                    />
                    <div className='relative w-full'>
                        <CustomInput
                            type={pwShow ? 'text' : 'password'}
                            value={loginInfo.password}
                            name={'password'}
                            placeholder={'비밀번호 입력해주세요'}
                            onChange={(e) =>
                                setLoginInfo({
                                    ...loginInfo,
                                    password: e.target.value
                                })
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    goLogin();
                                }
                            }}
                        />
                        <div
                            className='top-0 right-6 absolute flex items-center h-full cursor-pointer'
                            onClick={() => setPwShow((p) => !p)}
                        >
                            {pwShow ? <EyeOn /> : <EyeOff />}
                        </div>
                    </div>

                    <div className='h-12 mobile:h-8 text-red mobile:text-xs text-center'>
                        {
                            loginError &&
                            <>
                                <p>아이디 또는 비밀번호가 잘못 되었습니다.</p>
                                <p>아이디와 비밀번호를 정확히 입력해 주세요.</p>
                            </>
                        }
                    </div>

                    <div className='flex flex-col items-start gap-3 w-full'>
                        <CustomButton
                            className='bg-primary w-full'
                            disabled={!clear}
                            loading={loading}
                            title='로그인'
                            onClick={goLogin}
                        />
                        <label
                            htmlFor='keepCheck'
                            className='flex justify-between items-center gap-2 text-textGray mobile:text-sm text-lg cursor-pointer'>
                            {
                                keep ?
                                    <CheckBoxOn /> :
                                    <CheckBoxOff />
                            }
                            <input
                                className="hidden accent-primary"
                                type="checkbox"
                                name=""
                                id="keepCheck"
                                checked={keep}
                                onChange={() => setKeep(p => !p)}
                            />
                            로그인 상태유지
                        </label>
                    </div>
                </form>

                <div className='flex justify-between items-center mobile:px-4 w-[600px] mobile:w-full text-center'>
                    <p className='flex-1 text-textGray cursor-pointer' onClick={goPwRest}>비밀번호 재설정</p>
                    <Bar />
                    <p className='flex-1 text-primary cursor-pointer' onClick={goSignUp}>회원가입</p>
                </div>
            </div>
        </PageTemplate>
    );
}

export default Login;